import {testIdProps} from '@github-ui/test-id-props'
import type {LinkProps} from '@primer/react'
import {Box, CounterLabel, merge} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {ReactNode} from 'react'

export type SectionFilterLinkProps = Omit<LinkProps, 'as'> & {
  /**
   * The name of this section filter, e.g., "Open" to describe a section filter that shows only open issues.
   */
  title: string
  /**
   * The number of list items in the category this section filter represents. Optional. Changes appearance when
   * `isLoading` is true.
   */
  count?: string | number | ReactNode
  /**
   * The URL to load the list with only items matching this section filter.
   */
  href: string
  /**
   * Optionally specify whether this section filter is currently active. Defaults to not being selected.
   */
  isSelected?: boolean
  /**
   * Optionally specify whether the section filter is still being loaded. Will be used to style the count, if given,
   * when true.
   */
  isLoading?: boolean
}

/**
 * Represents a single categorical section filter. Used to allow a user to filter the ListView's list items to only
 * show those matching this filter.
 */
export const SectionFilterLink = ({
  href,
  title,
  count,
  isSelected = false,
  isLoading = false,
  sx: externalLinkSx = {},
  ...rest
}: SectionFilterLinkProps) => {
  let counterLabelSx: BetterSystemStyleObject | undefined
  if (typeof count !== 'undefined') {
    counterLabelSx = {
      ml: 2,
      lineHeight: '18px',
      py: 0,
      bg: 'neutral.muted',
      fontSize: 0,
      px: '6px',
      display: 'inline-block',
      fontWeight: 'semibold',
      minWidth: 20,
      textAlign: 'center',
    }
    if (isLoading) {
      counterLabelSx.color = 'transparent'
    }
  }
  const defaultSx = {
    color: isSelected ? 'fg.default' : 'fg.muted',
    fontWeight: isSelected ? 'bold' : 'normal',
    fontSize: 1,
    px: 2,
    py: 0,
    borderRadius: 2,
    textAlign: 'center',
    lineHeight: '30px',
    display: 'flex',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    transition: 'background .12s ease-out',
    '&:hover:not([disabled])': {
      bg: 'actionListItem.default.hoverBg',
      textDecoration: 'none',
    },
    '&:active:not([disabled])': {
      bg: 'actionListItem.default.active',
      borderColor: 'btn.activeBorder',
    },
  }
  const sx = externalLinkSx ? merge<BetterSystemStyleObject>(defaultSx, externalLinkSx) : defaultSx
  return (
    <Box
      as="a"
      href={href}
      sx={sx}
      {...testIdProps('list-view-section-filter-link')}
      aria-current={isSelected ? 'true' : undefined}
      {...rest}
    >
      <Box sx={{display: 'block'}}>{title}</Box>
      {typeof count !== 'undefined' && <CounterLabel sx={counterLabelSx}>{count}</CounterLabel>}
    </Box>
  )
}

try{ SectionFilterLink.displayName ||= 'SectionFilterLink' } catch {}